import React, { useState } from 'react';
import { useUser } from '../providers/UserProvider';
import { useToast } from '../../hooks/useToast';
import gun from '../../config/gun.config';
import { AvatarDisplay } from '../avatar/AvatarDisplay';
import { AvatarBuilder } from '../avatar/AvatarBuilder';
import { QRProfileCard } from './QRProfileCard';
import { MapControls } from '../map/MapControls';
import { LocationPrecision } from '../../hooks/useLocation';
import { INTEREST_TAGS } from '../../types/user';
import { Theme } from '../../hooks/useTheme';
import { NotifPrefs } from '../../hooks/useNotifPrefs';
import { IconCopy, IconCheck } from '../ui/Icons';
import { track } from '../../analytics';
import { t } from '../../i18n';

interface Props {
  precision: LocationPrecision;
  onPrecisionChange: (p: LocationPrecision) => void;
  radius: number | null;
  onRadiusChange: (r: number | null) => void;
  onSignOut?: () => void;
  theme?: Theme;
  onToggleTheme?: () => void;
  notifPrefs?: NotifPrefs;
  onNotifPrefsChange?: (partial: Partial<NotifPrefs>) => void;
}

// Reusable inline toggle (used for both master and sub-rows)
function Toggle({ on, onChange, disabled }: { on: boolean; onChange: () => void; disabled?: boolean }) {
  return (
    <button
      onClick={onChange}
      disabled={disabled}
      className={`w-10 h-6 rounded-full transition-colors relative flex-shrink-0 ${
        disabled ? 'opacity-40 cursor-not-allowed' : ''
      } ${on && !disabled ? 'bg-stabeon-accent' : 'bg-stabeon-border'}`}
    >
      <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
        on ? 'translate-x-4' : 'translate-x-0.5'
      }`} />
    </button>
  );
}

export function SettingsPanel({ precision, onPrecisionChange, radius, onRadiusChange, onSignOut, theme, onToggleTheme, notifPrefs, onNotifPrefsChange }: Props) {
  const { user, updateUser, logout } = useUser();
  const { show } = useToast();
  const [editingAvatar, setEditingAvatar] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [nickname, setNickname] = useState(user?.nickname || '');
  const [nicknameDirty, setNicknameDirty] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [bio, setBio] = useState(user?.bio || '');
  const [bioDirty, setBioDirty] = useState(false);
  const [inviteCopied, setInviteCopied] = useState(false);

  // Keep local input in sync when user.nickname changes externally (e.g. GUN
  // sync on session restore) — but only when the field is not actively being edited.
  React.useEffect(() => {
    if (!nicknameDirty && user?.nickname) {
      setNickname(user.nickname);
    }
  }, [user?.nickname]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => { if (!bioDirty && user?.bio !== undefined) setBio(user.bio || ''); }, [user?.bio]); // eslint-disable-line

  const saveBio = () => {
    const trimmed = bio.trim();
    if (!bioDirty) return;
    try {
      const raw = localStorage.getItem('stabeon_session');
      if (raw) { const obj = JSON.parse(raw); obj.bio = trimmed; localStorage.setItem('stabeon_session', JSON.stringify(obj)); }
    } catch {}
    if (user?.nullifierHash) gun.get('stabeon_users').get(user.nullifierHash).get('bio').put(trimmed);
    updateUser({ bio: trimmed });
    setBioDirty(false);
    show('Bio saved', 'success');
  };

  const saveNickname = () => {
    const trimmed = nickname.trim();
    console.log('[saveNickname] trimmed:', trimmed, 'dirty:', nicknameDirty, 'user:', user?.nullifierHash?.slice(-6));
    if (!trimmed || !nicknameDirty) return;

    // ── 1. Patch localStorage directly (guaranteed, doesn't depend on persistUser) ──
    try {
      const raw = localStorage.getItem('stabeon_session');
      if (raw) {
        const obj = JSON.parse(raw);
        obj.nickname = trimmed;
        localStorage.setItem('stabeon_session', JSON.stringify(obj));
        console.log('[saveNickname] localStorage patched ✓');
      }
    } catch (e) {
      console.warn('[saveNickname] localStorage patch failed', e);
    }

    // ── 2. Direct GUN field write (most reliable, no full-object put issues) ──
    if (user?.nullifierHash) {
      gun.get('stabeon_users').get(user.nullifierHash).get('nickname').put(trimmed);
      console.log('[saveNickname] GUN field write queued ✓');
    }

    // ── 3. Update React context (propagates to all consumers) ──
    updateUser({ nickname: trimmed });

    setNicknameDirty(false);
    show('Nickname saved', 'success');
  };

  const shareInvite = async () => {
    const base = window.location.origin;
    const url = `${base}?utm_source=referral&utm_medium=invite&utm_campaign=friend_invite&ref=${user!.nullifierHash}&shared_by=${user!.nullifierHash}`;
    const text = t('invite_text');
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Join Joystance', text, url });
        track('invite_shared', { method: 'native_share' });
      } else {
        await navigator.clipboard.writeText(`${text}\n${url}`);
        setInviteCopied(true);
        setTimeout(() => setInviteCopied(false), 2500);
        track('invite_shared', { method: 'clipboard' });
      }
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        // Fallback: try clipboard
        try {
          await navigator.clipboard.writeText(`${text}\n${url}`);
          setInviteCopied(true);
          setTimeout(() => setInviteCopied(false), 2500);
          track('invite_shared', { method: 'clipboard_fallback' });
        } catch {}
      }
    }
  };

  if (!user) return null;

  if (editingAvatar) {
    return (
      <AvatarBuilder
        initial={user.avatar}
        onSave={avatar => { updateUser({ avatar }); setEditingAvatar(false); show('Avatar saved', 'success'); }}
        onClose={() => setEditingAvatar(false)}
      />
    );
  }

  if (showQR) {
    return <QRProfileCard userId={user.nullifierHash} nickname={user.nickname} onClose={() => setShowQR(false)} />;
  }

  const copyCode = async () => {
    if (!user.recoveryCode) return;
    try {
      await navigator.clipboard.writeText(user.recoveryCode);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    } catch {}
  };

  return (
    <div className="flex flex-col gap-5 pb-8">
      {/* ── INVITE ───────────────────────────── */}
      <button
        onClick={shareInvite}
        className="w-full flex items-center gap-4 px-4 py-4 rounded-2xl transition-all active:scale-[0.98]"
        style={{ background: 'linear-gradient(135deg, #7c3aed22 0%, #c084fc18 100%)', border: '1px solid #7c3aed44' }}
      >
        <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #c084fc 100%)' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
            <polyline points="16 6 12 2 8 6"/>
            <line x1="12" y1="2" x2="12" y2="15"/>
          </svg>
        </div>
        <div className="flex-1 text-left">
          <p className="text-stabeon-text font-semibold text-sm">{t('invite_title')}</p>
          <p className="text-stabeon-muted text-[11px] mt-0.5">{t('invite_sub')}</p>
        </div>
        <div className="flex-shrink-0 flex items-center gap-1.5">
          {inviteCopied ? (
            <span className="text-stabeon-online text-xs font-semibold">{t('invite_copied')}</span>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="text-stabeon-accent">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          )}
        </div>
      </button>

      {/* ── ACCOUNT ──────────────────────────── */}
      <div>
        <p className="section-label px-1 mb-2">Account</p>
        <div className="card overflow-hidden divide-y divide-stabeon-border/50">
          {/* Profile row */}
          <div className="flex items-center gap-4 px-4 py-4">
            <button onClick={() => setEditingAvatar(true)} className="relative group active:opacity-70 transition-opacity flex-shrink-0">
              <AvatarDisplay avatar={user.avatar} size={56} isMe />
              {/* Edit badge — always visible on mobile, hover on desktop */}
              <span className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-stabeon-accent border-2 border-stabeon-bg flex items-center justify-center text-[10px]">
                ✏️
              </span>
            </button>
            <div className="flex-1">
              <div className="relative flex items-center gap-2">
                <div className="flex-1 relative">
                  <input
                    value={nickname}
                    onChange={e => { setNickname(e.target.value.slice(0, 24)); setNicknameDirty(true); }}
                    onKeyDown={e => e.key === 'Enter' && saveNickname()}
                    maxLength={24}
                    className="bg-transparent text-stabeon-text font-semibold text-base focus:outline-none border-b border-transparent focus:border-stabeon-accent transition-colors w-full pr-10"
                  />
                  <span className={`absolute right-0 bottom-1 text-[10px] font-mono ${nickname.length > 20 ? 'text-stabeon-danger' : 'text-stabeon-muted/50'}`}>
                    {nickname.length}/24
                  </span>
                </div>
                {nicknameDirty && (
                  <button
                    onClick={saveNickname}
                    disabled={!nickname.trim()}
                    className="flex-shrink-0 px-2.5 py-1 rounded-lg bg-stabeon-accent text-white text-xs font-semibold transition-opacity disabled:opacity-40 hover:opacity-90 active:opacity-70"
                  >
                    Save
                  </button>
                )}
              </div>
              <p className="text-stabeon-muted text-[10px] mt-1">
                {nicknameDirty ? 'Press Save or Enter to confirm' : 'Tap name or avatar to edit'}
              </p>
            </div>
          </div>

          {/* Bio */}
          <div className="px-4 py-3">
            <p className="text-stabeon-text text-sm font-medium mb-1">Bio</p>
            <textarea
              value={bio}
              onChange={e => { setBio(e.target.value.slice(0, 120)); setBioDirty(true); }}
              placeholder="Tell people a little about yourself…"
              rows={2}
              className="w-full input-field text-sm resize-none"
            />
            <div className="flex items-center justify-between mt-1.5">
              <span className={`text-[10px] ${bio.length > 100 ? 'text-stabeon-danger' : 'text-stabeon-muted/50'}`}>{bio.length}/120</span>
              {bioDirty && (
                <button onClick={saveBio} className="px-2.5 py-1 rounded-lg bg-stabeon-accent text-white text-xs font-semibold">Save</button>
              )}
            </div>
          </div>

          {/* Recovery code */}
          {user.recoveryCode && (
            <div className="px-4 py-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-stabeon-text text-sm font-medium">Recovery code</p>
                  <p className="text-stabeon-muted text-[10px] mt-0.5">Login from another device</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-stabeon-text text-sm font-mono font-bold tracking-[0.2em]">
                    {user.recoveryCode.slice(0, 4)} {user.recoveryCode.slice(4)}
                  </span>
                  <button
                    onClick={copyCode}
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-stabeon-muted hover:text-stabeon-accent transition-colors"
                  >
                    {codeCopied ? <IconCheck size={16} className="text-stabeon-online" /> : <IconCopy size={16} />}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* QR Profile Card */}
          <button
            onClick={() => setShowQR(true)}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-stabeon-card/60 transition-colors"
          >
            <div>
              <p className="text-stabeon-text text-sm font-medium text-left">QR Profile Card</p>
              <p className="text-stabeon-muted text-[10px] mt-0.5">Share your profile with a QR code</p>
            </div>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="text-stabeon-muted flex-shrink-0">
              <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
              <rect x="3" y="14" width="7" height="7" rx="1"/>
              <path d="M14 14h3v3m0 4v-4m4 0h-4m4-3v3"/>
            </svg>
          </button>

          {/* Account ID */}
          <div className="px-4 py-3 flex items-center justify-between">
            <p className="text-stabeon-text text-sm font-medium">Account ID</p>
            <p className="text-stabeon-muted text-[11px] font-mono">{user.nullifierHash.slice(0, 12)}…</p>
          </div>
        </div>

        <button onClick={onSignOut || logout} className="btn-danger w-full mt-3">
          Sign out
        </button>
      </div>

      {/* ── PREFERENCES ──────────────────────── */}
      <div>
        <p className="section-label px-1 mb-2">Preferences</p>
        <div className="flex flex-col gap-3">

          {/* Appearance */}
          {onToggleTheme && (
            <button
              onClick={onToggleTheme}
              className="w-full flex items-center justify-between px-4 py-3 card transition-all hover:border-stabeon-accent/40"
            >
              <div className="flex items-center gap-3">
                <span className="text-lg">{theme === 'light' ? '☀️' : '🌙'}</span>
                <div className="text-left">
                  <p className="text-stabeon-text text-sm font-medium">Appearance</p>
                  <p className="text-stabeon-muted text-[10px]">{theme === 'light' ? 'Light mode' : 'Dark mode'}</p>
                </div>
              </div>
              <Toggle on={theme === 'light'} onChange={onToggleTheme} />
            </button>
          )}

          {/* Notifications */}
          {notifPrefs && onNotifPrefsChange && (
            <div className="card overflow-hidden">
              <button
                onClick={() => onNotifPrefsChange({ all: !notifPrefs.all })}
                className="w-full flex items-center justify-between px-4 py-3 transition-all hover:bg-stabeon-card/60"
              >
                <div className="flex items-center gap-3">
                  <span className="text-lg">🔔</span>
                  <div className="text-left">
                    <p className="text-stabeon-text text-sm font-medium">Notifications</p>
                    <p className="text-stabeon-muted text-[10px]">{notifPrefs.all ? 'Receiving alerts' : 'All alerts muted'}</p>
                  </div>
                </div>
                <Toggle on={notifPrefs.all} onChange={() => onNotifPrefsChange({ all: !notifPrefs.all })} />
              </button>
              <div className={`border-t border-stabeon-border/50 transition-opacity ${notifPrefs.all ? 'opacity-100' : 'opacity-40'}`}>
                {([
                  { key: 'messages' as const,   icon: '💬', label: 'Messages',       desc: 'Direct messages from people nearby' },
                  { key: 'events' as const,      icon: '🎉', label: 'Events',          desc: 'Announcements & happening-now alerts' },
                  { key: 'nearbyPins' as const,  icon: '📍', label: 'Nearby activity', desc: 'New pins dropped in your area' },
                ]).map(({ key, icon, label, desc }, i, arr) => (
                  <button
                    key={key}
                    disabled={!notifPrefs.all}
                    onClick={() => onNotifPrefsChange({ [key]: !notifPrefs[key] })}
                    className={`w-full flex items-center justify-between px-4 py-2.5 transition-all hover:bg-stabeon-card/60 ${i < arr.length - 1 ? 'border-b border-stabeon-border/30' : ''} ${!notifPrefs.all ? 'cursor-not-allowed' : ''}`}
                  >
                    <div className="flex items-center gap-3 pl-1">
                      <span className="text-base w-5 text-center">{icon}</span>
                      <div className="text-left">
                        <p className="text-stabeon-text text-xs font-medium leading-tight">{label}</p>
                        <p className="text-stabeon-muted text-[10px] leading-tight mt-0.5">{desc}</p>
                      </div>
                    </div>
                    <Toggle on={notifPrefs[key]} onChange={() => onNotifPrefsChange({ [key]: !notifPrefs[key] })} disabled={!notifPrefs.all} />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Interest tags */}
          <div className="card px-4 py-3">
            <p className="text-stabeon-text text-sm font-medium mb-1">Interests</p>
            <p className="text-stabeon-muted text-[10px] mb-3">Highlights matching events in your feed</p>
            <div className="flex flex-wrap gap-1.5">
              {INTEREST_TAGS.map(tag => {
                const selected = (user.interests || []).includes(tag);
                return (
                  <button
                    key={tag}
                    onClick={() => {
                      const current = user.interests || [];
                      const next = selected ? current.filter(t => t !== tag) : [...current, tag];
                      updateUser({ interests: next });
                    }}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all ${selected ? 'pill-active' : 'pill-inactive'}`}
                  >
                    {tag}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Map & privacy */}
          <div className="card px-4 py-3">
            <p className="text-stabeon-text text-sm font-medium mb-3">Location & Privacy</p>
            <MapControls
              precision={precision}
              onPrecisionChange={onPrecisionChange}
              radius={radius}
              onRadiusChange={onRadiusChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
